import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { cognitoEnabled, logoutUrl } from '../lib/cognito.js';
import { initials } from '../lib/format.js';
import { Badge } from '../components/badge.js';

export function MeScreen() {
  const user = useAuth((s) => s.user);
  const clearSession = useAuth((s) => s.clear);
  // Cognito sign-out must also clear the Hosted-UI session cookie, or the
  // next "Sign in" silently logs straight back in.
  const clear = () => {
    const target = cognitoEnabled() ? logoutUrl() : null;
    clearSession();
    if (target) window.location.assign(target);
  };
  const health = useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => api.health.liveness(signal),
    refetchInterval: 30_000,
  });

  if (!user) return null;

  return (
    <>
      <div className="topbar">
        <h2>Me</h2>
      </div>

      <div className="kd-card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="avatar" style={{ width: 52, height: 52, fontSize: 17 }}>
          {initials(user.displayName)}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--kd-navy)' }}>{user.displayName}</div>
          <div className="kd-muted">{user.email}</div>
          <div className="kd-muted" style={{ textTransform: 'capitalize' }}>{user.role}</div>
        </div>
      </div>

      <div className="section-title">Access</div>
      <div className="kd-card">
        <Row label="Salesperson code" value={user.spireSalespersonCode ?? '—'} mono />
        <Row label="Can view cost" value={<Flag on={user.canViewCost} />} />
        <Row label="Can view margin" value={<Flag on={user.canViewMargin} />} />
      </div>

      <div className="kd-card" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: health.isSuccess ? 'var(--kd-green)' : health.isError ? 'var(--kd-danger)' : '#cbd5e1',
            display: 'inline-block',
          }}
        />
        <span className="kd-muted">
          {health.isPending ? 'Checking connection…' : health.isError ? 'Spire bridge unreachable' : 'Connected to Spire'}
        </span>
      </div>

      {user.role === 'admin' && (
        <>
          <Link
            to="/admin"
            className="kd-card"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', textDecoration: 'none', color: 'var(--kd-navy)', fontWeight: 600 }}
          >
            <span>Manage users</span>
            <span style={{ color: 'var(--kd-text-3)' }}>›</span>
          </Link>
          <Link
            to="/settings"
            className="kd-card"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', textDecoration: 'none', color: 'var(--kd-navy)', fontWeight: 600 }}
          >
            <span>App settings</span>
            <span style={{ color: 'var(--kd-text-3)' }}>›</span>
          </Link>
        </>
      )}

      <button
        type="button"
        className="btn btn-secondary"
        style={{ margin: 14, width: 'calc(100% - 28px)' }}
        onClick={clear}
      >
        Sign out
      </button>
    </>
  );
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 14 }}>
      <span className="kd-muted">{label}</span>
      <span style={{ color: 'var(--kd-text)', fontFamily: mono ? 'monospace' : undefined, fontWeight: 600 }}>
        {value}
      </span>
    </div>
  );
}

function Flag({ on }: { on: boolean }) {
  return <Badge variant={on ? 'good' : 'neutral'}>{on ? 'Yes' : 'No'}</Badge>;
}
